<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { isAxiosError } from 'axios'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import moment from 'moment'
import { formatPhoneNumber } from '@/utils/phoneFormatter'
import { useI18n } from 'vue-i18n'
import { useFilterStore } from '@/stores/filterStore'
import { storeToRefs } from 'pinia'
import {
  getZBEdgeUsersWithFilter,
  type ZBEdgeUserSortBy,
  type ZBEdgeUserSortOrder,
  type ZBEdgeUser
} from '@/api/endpoints/zbedgeUserWeb'
import type { PhoneTelNumberInputModel, VueTelInputData } from '@/types/phoneTelNumberInputTypes'

const router = useRouter()
const $q = useQuasar()
const filterStore = useFilterStore()
const { clearZBEdgeUserFilterCount } = storeToRefs(filterStore)
const { t } = useI18n()

const userTable = ref()
const form = ref()
const loading = ref(false)
const showResults = ref(false)

const firstNameModel = ref('')
const lastNameModel = ref('')
const emailModel = ref('')
const phoneTelNumberInputModel = ref<PhoneTelNumberInputModel>({
  value: '',
  phoneObject: {} as VueTelInputData
})
const phoneInputRef = ref(null)

const userTableColumns = ref([
  { name: 'firstName', field: 'firstName', sortable: true, align: 'left' },
  { name: 'lastName', field: 'lastName', sortable: true, align: 'left' },
  { name: 'email', field: 'email', sortable: true, align: 'left' },
  { name: 'phoneNumber', field: 'phoneNumber', sortable: false, align: 'left' },
  { name: 'createdDate', field: 'createdDate', sortable: true, align: 'left' }
])
const userTableRows = ref<ZBEdgeUser[]>([])

const initialPagination = ref({
  sortBy: 'firstName',
  descending: false,
  page: 1,
  rowsPerPage: 15,
  rowsNumber: 0
})

watch(clearZBEdgeUserFilterCount, () => onCancel())

nextTick(() => form.value?.resetValidation())

const getSortBy = (sortBy: string): ZBEdgeUserSortBy => {
  switch (sortBy) {
    case 'firstName':
      return 'FirstName'
    case 'lastName':
      return 'LastName'
    case 'email':
      return 'Email'
    case 'createdDate':
      return 'CreatedDate'
    default:
      return 'FirstName'
  }
}

const getSortOrder = (descending: boolean): ZBEdgeUserSortOrder =>
  descending ? 'Desc' : 'Asc'

const normalizeOrNull = (value: string) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}


const formatDate = (value: string | null) => {
  if (!value) {
    return ''
  }

  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('LLLL') : value
}

const loadUsers = async (page: number, rowsPerPage: number, sortBy: string, descending: boolean) => {
  const sort = getSortBy(sortBy)
  $q.loading.show()
  try {
    const response = await getZBEdgeUsersWithFilter({
      pageNumber: page,
      pageSize: rowsPerPage,
      sortBy: sort,
      sortOrder: getSortOrder(descending),
      firstName: normalizeOrNull(firstNameModel.value),
      lastName: normalizeOrNull(lastNameModel.value),
      email: normalizeOrNull(emailModel.value),
      phoneNumber: phoneTelNumberInputModel.value?.phoneObject?.nationalNumber ?? null,
      phoneCountryCode: null
    })

    const users = response.data?.users ?? []
    const mappedRows = users.map((user) => ({
      ...user,
      createdDate: formatDate(user.createdDate)
    }))

    userTableRows.value = mappedRows
    initialPagination.value.rowsNumber = response.data?.totalCount ?? 0
  } catch (error) {
    if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      window.location.href = '/Logout'
    }
  } finally {
    $q.loading.hide()
  }
}

function getFilterCount() {
  let count = 0

  if (firstNameModel.value.trim() !== '') count++
  if (lastNameModel.value.trim() !== '') count++
  if (emailModel.value.trim() !== '') count++
  if (phoneTelNumberInputModel.value.value !== '') count++

  return count
}

const onSubmit = async () => {
  const isValid = await form.value.validate()

  if (!isValid) {
    return
  }

  loading.value = true
  filterStore.zbedgeUserFilterCount = getFilterCount()
  initialPagination.value.page = 1

  try {
    await loadUsers(
      initialPagination.value.page,
      initialPagination.value.rowsPerPage,
      initialPagination.value.sortBy,
      initialPagination.value.descending
    )
    showResults.value = true
  } finally {
    loading.value = false
  }
}

const onCancel = () => {
  firstNameModel.value = ''
  lastNameModel.value = ''
  emailModel.value = ''
  phoneTelNumberInputModel.value.value = ''
  phoneTelNumberInputModel.value.phoneObject = {} as VueTelInputData

  userTableRows.value = []
  showResults.value = false

  initialPagination.value.sortBy = 'firstName'
  initialPagination.value.descending = false
  initialPagination.value.page = 1
  initialPagination.value.rowsNumber = 0
  filterStore.zbedgeUserFilterCount = 0

  nextTick(() => form.value?.resetValidation())
}

const onRowClick = (userId: string) => router.push({ name: 'ZBEdgeUser', params: { userId } })

const onRequest = async (props: any) => {
  const { page, rowsPerPage, sortBy, descending } = props.pagination

  loading.value = true

  try {
    await loadUsers(page, rowsPerPage, sortBy, descending)

    initialPagination.value.page = page
    initialPagination.value.sortBy = sortBy
    initialPagination.value.descending = descending
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <q-page padding>
    <div class="q-pb-xl">
      <h1>{{ $t('gen_zbedge_users') }}</h1>
    </div>

    <div class="row">
      <div class="col bg-white q-pa-xl rep-profile__form">
        <q-form @submit.prevent ref="form">
          <zbm-skeleton-loader height="22.5714285714rem" width="100%">
            <div class="row mb-20 rep-profile__fields">
              <div class="col q-pa-sm">
                <q-input outlined v-model="firstNameModel" :placeholder="$t('gen_first_name')" />
              </div>
              <div class="col q-pa-sm">
                <q-input outlined v-model="lastNameModel" :placeholder="$t('gen_last_name')" />
              </div>
              <div class="col q-pa-sm">
                <q-input outlined v-model="emailModel" :placeholder="$t('gen_email')" />
              </div>
            </div>
            <div class="row mb-20 rep-profile__fields">
              <div class="col q-pa-sm">
                <zbm-telephone-with-validation v-model="phoneTelNumberInputModel"
                                               :input-width="$zb.enums.FormElWidthEnum.qFormElFull" countryCode="US"
                                               :placeholder="$t('gen_phone')" ref="phoneInputRef" />
              </div>
              <div class="col q-pa-sm"></div>
              <div class="col q-pa-sm"></div>
            </div>

            <div class="row items-center q-col-gutter-x-sm rep-profile__buttons">
              <div class="col-auto q-ml-auto">
                <zbm-skeleton-loader height="2.8571428571rem" width="3.1428571429rem">
                  <zbm-btn
                    @click="onCancel"
                    :color="$zb.enums.ButtonStdColorEnum.darkTealText"
                    :height="$zb.enums.ButtonHeightEnum.md"
                    :label="$t('gen_clear_filters')"
                    :data-testid="'btnCancel'"
                  />
                </zbm-skeleton-loader>
              </div>

              <div class="col-auto">
                <zbm-skeleton-loader height="2.8571428571rem" width="11.5714285714rem">
                  <zbm-btn
                    @click="onSubmit"
                    :color="$zb.enums.ButtonStdColorEnum.darkTeal"
                    :height="$zb.enums.ButtonHeightEnum.md"
                    :label="$t('gen_search')"
                    :type="$zb.enums.ButtonTypeEnum.submit"
                    :data-testid="'btnSave'"
                  />
                </zbm-skeleton-loader>
              </div>
            </div>
          </zbm-skeleton-loader>
        </q-form>
      </div>
    </div>

    <div v-if="showResults">
      <zbm-table
        :columns="userTableColumns"
        :rows="userTableRows"
        row-key="userId"
        expandable
        :hide-pagination="false"
        ref="userTable"
        v-model:pagination="initialPagination"
        :loading="loading"
        @request="onRequest"
      >
        <template v-slot:header="props">
          <q-tr :props="props">
            <q-th key="firstName" :props="props" class="text-left">{{ $t('gen_first_name') }}</q-th>
            <q-th key="lastName" :props="props" class="text-left">{{ $t('gen_last_name') }}</q-th>
            <q-th key="email" :props="props" class="text-left">{{ $t('gen_email') }}</q-th>
            <q-th key="phoneNumber" :props="props" class="text-left">{{ $t('gen_phone') }}</q-th>
            <q-th key="createdDate" :props="props" class="text-left">{{ $t('gen_date_created') }}</q-th>
          </q-tr>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props" :key="`row-${props.row.userId}`" class="cursor-pointer" @click="onRowClick(props.row.userId)">
            <q-td key="firstName" :props="props" no-hover>
              {{ props.row.firstName }}
            </q-td>
            <q-td key="lastName" :props="props" no-hover>
              {{ props.row.lastName }}
            </q-td>
            <q-td key="email" :props="props" no-hover>
              {{ props.row.email }}
            </q-td>
            <q-td key="phoneNumber" :props="props" no-hover>
              {{ formatPhoneNumber(props.row.phoneCountryCode, props.row.phoneNumber, '') }}
            </q-td>
            <q-td key="createdDate" :props="props" no-hover>
              {{ props.row.createdDate }}
            </q-td>
          </q-tr>
        </template>
      </zbm-table>
    </div>
  </q-page>
</template>
